import { memo, useEffect, useState } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import * as Y from 'yjs';
import { SHAPE_COMPONENTS } from './shapes';
import styles from './component.module.css';
import fmtStyles from '../formatting.module.css';
import type { ShapeNodeData } from './types';
import { makeDefault } from './defaults';
import { withDefaults } from '../with-defaults';
import { SelectionTransformer } from '@/features/canvas/engine/selection/selection-transformer';
import { useNodeActions } from "@/features/canvas/engine/wiring/contexts/node-actions-context";
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { formattingVars } from '../../hooks/use-formatting-state';
import { resolveNodeAriaLabel } from '../../a11y/aria-labels';
import { useParentLabelResolver } from '../../a11y/parent-label-context';
import { NodeTextEditor } from '../node-text-editor';

const VBOX_W = 100;
const VBOX_H = 100;

function strokeDasharray(style: ShapeNodeData['strokeStyle']) {
  switch (style) {
    case 'dashed': return '6 4';
    case 'dotted': return '2 4';
    default: return undefined;
  }
}

type ShapeNodeShape = Node<ShapeNodeData, 'shape'>;

/**
 * SVG shape node: rectangle / ellipse / diamond / arrow-shape.
 * Double-click enters text editing mode — text is stored as Y.Text and
 * rendered as a centered overlay above the SVG.
 */
export default memo(function ShapeNode({ id, data, selected, dragging, parentId, width, height }: NodeProps<ShapeNodeShape>) {
  // Merge canonical defaults under the actual data so any field the bridge
  // (or a legacy doc) omits falls back to the design-system default rather
  // than rendering as undefined → invalid CSS. `text` stays whatever was
  // passed (Y.Text instance or null) since defaults.text is null.
  const d = withDefaults<ShapeNodeData>(data, makeDefault as () => ShapeNodeData);
  const dash = strokeDasharray(d.strokeStyle);
  const fillValue = d.fill === 'none' ? 'transparent' : d.fill;
  const ShapeVariant = SHAPE_COMPONENTS[d.shapeKind];
  const actions = useNodeActions();
  const doc = useCanvasDoc();
  const parentLabel = useParentLabelResolver();

  // The shared <svg> ALWAYS uses the static 100×100 viewBox (stretched by
  // preserveAspectRatio="none"). Do NOT switch the viewBox per node — a
  // per-node real-pixel viewBox caused a ResizeObserver measurement feedback
  // loop that froze the canvas on load.
  //
  // Pill is dimension-aware only in that it needs the node's W/H to
  // pre-compensate its corner radius for the stretch (so caps stay circular);
  // it still draws in the 100×100 box. width/height come from NodeProps (the
  // value RF puts on the node from our yjs `size`), mirroring emoji/freedraw.
  const isDimAware = d.shapeKind === 'pill';
  const variantDims = isDimAware ? { width, height } : {};
  const svgGeom = (
    <svg viewBox={`0 0 ${VBOX_W} ${VBOX_H}`} preserveAspectRatio="none">
      <ShapeVariant cornerRadius={d.cornerRadius} strokeDasharray={dash} {...variantDims} />
    </svg>
  );

  const text = d.text instanceof Y.Text ? d.text : null;
  const [editing, setEditing] = useState(false);

  const ariaLabel = resolveNodeAriaLabel(
    { type: 'shape', data: { shapeKind: d.shapeKind }, parentId: parentId ?? null },
    parentLabel,
  );

  // Exit edit mode when node is deselected.
  useEffect(() => {
    if (!selected) setEditing(false);
  }, [selected]);

  const shapeContent = (
    <div
      className={styles.shape}
      style={{
        '--shape-fill': fillValue,
        '--shape-stroke': d.stroke,
        '--shape-stroke-width': String(d.strokeWidth),
      } as React.CSSProperties}
    >
      {svgGeom}
    </div>
  );

  return (
    <SelectionTransformer
      nodeId={id}
      nodeType="shape"
      selected={!!selected}
      dragging={!!dragging}
      editing={editing}
      onExpand={actions?.onExpand}
      ghostContent={shapeContent}
      doc={doc ?? undefined}
    >
      {/* React Flow node container — see engine/README §Raw-HTML exception */}
      <div
        className={styles.shape}
        data-testid="shape-node"
        data-node-id={id}
        data-shape-kind={d.shapeKind}
        data-selected={selected || undefined}
        data-editing={editing || undefined}
        aria-label={ariaLabel}
        style={{
          '--shape-fill': fillValue,
          '--shape-stroke': d.stroke,
          '--shape-stroke-width': String(d.strokeWidth),
          '--shape-fill-opacity': String(d.fillOpacity),
          '--shape-stroke-opacity': String(d.strokeOpacity),
          ...formattingVars({ fontSize: d.fontSize, fontFamily: d.fontFamily, textAlign: d.textAlign, verticalAlign: d.verticalAlign, bold: d.bold, italic: d.italic, underline: d.underline, strikethrough: d.strikethrough, textColor: d.textColor }),
        } as React.CSSProperties}
        onDoubleClick={() => { if (text) setEditing(true); }}
      >
        {svgGeom}
        {text !== null && (
          <NodeTextEditor
            ytext={text}
            editing={editing}
            ariaLabel="shape text"
            className={editing ? `${styles.editor} ${fmtStyles.formattingText} nodrag` : `${styles.editor} ${fmtStyles.formattingText}`}
            onBlur={() => setEditing(false)}
          />
        )}
      </div>
    </SelectionTransformer>
  );
});
